/**
 * SecureEmbed Tier 2 Export Page
 *
 * Enhanced with live preview, dataset filter, and download history
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Download, FileJson, FileSpreadsheet, Calendar, Database,
  AlertCircle, Loader2, CheckCircle, Clock
} from 'lucide-react';
import { exportApi, ExportResponse, insightsApi } from '../services/api';
import TierGate from '../components/TierGate';
import DataSourceBadge from '../components/DataSourceBadge';
import InfoTooltip from '../components/InfoTooltip';

interface PreviewRow {
  insight_id: string;
  insight_type: string;
  created_at: string;
  confidence?: number;
  source_embedding_id?: string;
}

interface DownloadRecord {
  id: string;
  timestamp: string;
  format: string;
  records: number;
  type: string;
}

const Export: React.FC = () => {
  const [format, setFormat] = useState<'json' | 'csv'>('json');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [insightType, setInsightType] = useState('');
  const [datasource, setDatasource] = useState('');
  const [maxRecords, setMaxRecords] = useState(10000);
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResponse | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Live preview
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Download history
  const [downloads, setDownloads] = useState<DownloadRecord[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('secureembed_downloads') || '[]');
    } catch { return []; }
  });

  // Fetch live preview data
  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const result = await insightsApi.listInsights({
        insight_type: insightType || undefined,
        limit: 8,
      });
      if (result?.success && result?.data?.insights) {
        setPreviewData(result.data.insights.map(i => ({
          insight_id: i.insight_id,
          insight_type: i.insight_type,
          created_at: i.created_at,
          confidence: i.provenance?.confidence,
          source_embedding_id: i.source_embedding_id,
        })));
      }
    } catch {
      // Keep empty
    } finally {
      setPreviewLoading(false);
    }
  }, [insightType]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleExport = async () => {
    setIsExporting(true);
    setExportResult(null);
    setExportError(null);

    try {
      const result = await exportApi.createExport({
        format,
        start_date: dateRange.start ? new Date(dateRange.start).toISOString() : undefined,
        end_date: dateRange.end ? new Date(dateRange.end).toISOString() : undefined,
        insight_type: insightType || undefined,
        datasource: datasource || undefined,
        max_records: maxRecords,
      });

      setExportResult(result);

      if (result.success && result.data?.content) {
        const blob = new Blob([result.data.content], {
          type: format === 'json' ? 'application/json' : 'text/csv',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Save to history
        const newDownload: DownloadRecord = {
          id: `dl_${Date.now()}`,
          timestamp: new Date().toISOString(),
          format,
          records: result.data.record_count,
          type: insightType || 'all',
        };
        const updated = [newDownload, ...downloads].slice(0, 20);
        setDownloads(updated);
        localStorage.setItem('secureembed_downloads', JSON.stringify(updated));
      }
    } catch (err: any) {
      setExportError(err.response?.data?.message || err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const getConfidenceColor = (c: number) => c >= 0.9 ? 'text-green-600' : c >= 0.8 ? 'text-amber-600' : 'text-red-600';

  return (
    <TierGate requiredTier={2}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Export Data</h2>
          <p className="text-gray-500 mt-1">Generate exports of your insights data</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Format selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">Export Format <InfoTooltip tooltipId="export.format" /></label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setFormat('json')}
                  className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                    format === 'json' ? 'border-[#2B064F] bg-purple-50 text-[#2B064F]' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FileJson className="w-5 h-5" />
                  <span className="font-medium">JSON</span>
                </button>
                <button
                  onClick={() => setFormat('csv')}
                  className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                    format === 'csv' ? 'border-[#2B064F] bg-purple-50 text-[#2B064F]' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  <span className="font-medium">CSV</span>
                </button>
              </div>
            </div>

            {/* Insight type filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Insight Type</label>
              <select
                value={insightType}
                onChange={(e) => setInsightType(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Types</option>
                <option value="anomaly_detection">Anomaly Detection</option>
                <option value="pattern_recognition">Pattern Recognition</option>
                <option value="cluster_analysis">Cluster Analysis</option>
                <option value="similarity_score">Similarity Score</option>
                <option value="trend_analysis">Trend Analysis</option>
                <option value="correlation">Correlation</option>
                <option value="aggregation">Aggregation</option>
              </select>
            </div>

            {/* Datasource filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Source</label>
              <select
                value={datasource}
                onChange={(e) => setDatasource(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Sources</option>
                <option value="aemo">AEMO Energy</option>
                <option value="bom">BOM Weather</option>
                <option value="aer">AER Regulatory</option>
              </select>
            </div>

            {/* Max records */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Records</label>
              <input
                type="number"
                value={maxRecords}
                onChange={(e) => setMaxRecords(parseInt(e.target.value))}
                min={100}
                max={50000}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Date range */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <span className="text-gray-500">to</span>
                <div className="relative flex-1">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-4">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                isExporting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#2B064F] hover:bg-[#3d0a6e] text-white'
              }`}
            >
              {isExporting ? (
                <><Loader2 className="w-5 h-5 animate-spin" />Generating...</>
              ) : (
                <><Download className="w-5 h-5" />Generate Export</>
              )}
            </button>

            {exportResult?.success && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">
                  Export complete - {exportResult.data.record_count} records downloaded
                </span>
              </div>
            )}

            {exportError && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-medium">{exportError}</span>
              </div>
            )}
          </div>
        </div>

        {/* Live Data Preview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-600" />
              <span className="font-semibold text-gray-900">Data Preview</span>
              {previewLoading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
              {!previewLoading && (
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                  {previewData.length} records (live)
                </span>
              )}
            </div>
            {datasource && <DataSourceBadge source={datasource} />}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-600"><span className="flex items-center gap-1">ID <InfoTooltip tooltipId="export.preview.id" /></span></th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Created</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600"><span className="flex items-center gap-1">Type <InfoTooltip tooltipId="export.preview.type" /></span></th>
                  <th className="text-right py-3 px-4 font-medium text-gray-600"><span className="flex items-center justify-end gap-1">Confidence <InfoTooltip tooltipId="export.preview.confidence" /></span></th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((row) => (
                  <tr key={row.insight_id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-xs">{row.insight_id.slice(0, 20)}...</td>
                    <td className="py-3 px-4 text-gray-600">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {row.insight_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      {row.confidence != null && (
                        <span className={`font-medium ${getConfidenceColor(row.confidence)}`}>
                          {(row.confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {previewData.length === 0 && !previewLoading && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-gray-400">No preview data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Download History */}
        {downloads.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                <span className="font-semibold text-gray-900">Download History</span>
              </div>
              <button
                onClick={() => { setDownloads([]); localStorage.removeItem('secureembed_downloads'); }}
                className="text-xs text-gray-400 hover:text-red-500"
              >
                Clear
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {downloads.map(dl => (
                <div key={dl.id} className="p-3 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    {dl.format === 'json' ? <FileJson className="w-4 h-4 text-blue-500" /> : <FileSpreadsheet className="w-4 h-4 text-green-500" />}
                    <span className="text-gray-700">{dl.records} records ({dl.format.toUpperCase()})</span>
                    <span className="text-xs text-gray-400">{dl.type === 'all' ? 'All types' : dl.type}</span>
                  </div>
                  <span className="text-xs text-gray-400">{new Date(dl.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TierGate>
  );
};

export default Export;
